import React, { useState, useRef, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { MATFILE_ENDLESS_TYPE, AUTOFILL_PATTERN } from 'pxc-editor-core/constants';
import {
  AUTOFILL_MODE,
  AUTOFILL_SETTING_LOOP,
} from '@/constants';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import {
  fillSections,
  setOtherContentState,
  setAutoFillAndCopyPattern,
} from '@/actions';
import { getGroups, isNextCellDisabled } from '@/helpers/helpers';
import { getOnlyDiffProps } from '@/helpers/common';
import AutofillAndCopyForm from './AutofillAndCopyForm.jsx';

const defaultData = {
  start: '1',
  end: '1',
  steps: 1,
  prefix: '',
  suffix: '',
  copiesNo: 1,
  mode: AUTOFILL_MODE,
  isSettingsVisible: false,
  setting: AUTOFILL_SETTING_LOOP,
  loopsAmount: 1,
  repeatsAmount: 1,
  isMatrix: false
};

const AutofillAndCopyFormContainer = props => {
  const {
    isPatternSelectAvailable,
    fillSections,
    isFromWizard,
    compRef,
    includeOtherContent,
    setOtherContentState,
    isNextCellPatternDisabled,
    setAutoFillAndCopyPattern,
    disabled,
    autoFillAndCopyPattern,
    isMatrix
  } = props;

  const formCompRef = useRef();
  const onChangeRef = useRef();
  const [changes, setChanges] = useState({});

  useEffect(() => {
    if(autoFillAndCopyPattern) return;

    if(isNextCellPatternDisabled) {
      setAutoFillAndCopyPattern(AUTOFILL_PATTERN.NEXT_PAGE);
    } else {
      setAutoFillAndCopyPattern(AUTOFILL_PATTERN.NEXT_CELL);
    }
  }, [isNextCellPatternDisabled])

  // define external api
  compRef.current = {
    hasChanges: () => {
      return !!Object.keys(changes).length
    },
    submit: () => {
      if (!isFromWizard) {
        const payload = { ...defaultData, ...changes, pattern: autoFillAndCopyPattern, isMatrix };
        fillSections(payload)
      }
    },
    validate: () => {
      return !formCompRef.current || formCompRef.current.validate();
    }
  };

  onChangeRef.current = (data) => {
    const newChanges = getOnlyDiffProps(defaultData, {...changes, ...data});

    if (Object.keys(data).length) {
      Object.keys(data).forEach(key => {
        defaultData[key] = data[key];
      })
    }

    setChanges(newChanges);
  };

  const cachedOnChange = useCallback((data) => onChangeRef.current(data), []);

  return (
    <AutofillAndCopyForm
      isPatternSelectAvailable={isPatternSelectAvailable}
      {...defaultData}
      {...changes}
      pattern={autoFillAndCopyPattern}
      disabled={disabled}
      isMatrix={isMatrix}
      compRef={formCompRef}
      onChange={cachedOnChange}
      includeOtherContent={includeOtherContent}
      setOtherContentState={setOtherContentState}
      isNextCellPatternDisabled={isNextCellPatternDisabled}
      setAutoFillAndCopyPattern={setAutoFillAndCopyPattern}
    />
  );
};

const mapStateToProps = state => {
  const selectedPage = state.core.pages[state.core.pagesSelection[0]];
  const pagesGroups = state.core.pagesSelection.length ? getGroups({[state.core.pagesSelection[0]]: selectedPage}) : getGroups(state.core.pages);
  return {
    isFromWizard: state.core.isFromWizard,
    includeOtherContent: state.core.includeOtherContent,
    pagesGroups,
    shieldsSelection: state.core.shieldsSelection,
    autoFillAndCopyPattern: state.core.autoFillAndCopyPattern,
  };
};

const mapDispatchToProps = dispatch => ({
  fillSections: (data) => dispatch(fillSections(data)),
  setOtherContentState: (checked) => dispatch(setOtherContentState(checked)),
  setAutoFillAndCopyPattern: (pattern) => dispatch(setAutoFillAndCopyPattern(pattern)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const matfileTemplate = getBuildedMatfileTemplate();
  let isPatternSelectAvailable = true;
  if (matfileTemplate) {
    if (matfileTemplate.type === MATFILE_ENDLESS_TYPE) {
      isPatternSelectAvailable = false;
    }
    else {
      let count = 0;
      matfileTemplate.layouts.forEach(layout => {
        layout.groups.forEach(gr => {
          count += gr.shields.length;
        });
      });
      isPatternSelectAvailable = count > 1;
    }
  }

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    isPatternSelectAvailable,
    isNextCellPatternDisabled: isNextCellDisabled(stateProps.pagesGroups, stateProps.shieldsSelection)
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(AutofillAndCopyFormContainer);